import { useState } from "react";
import { Box, Typography, Paper, Alert, Chip, IconButton, Tooltip } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { PageHeader } from "@/components/PageHeader";
import { DataGrid, type GridColDef } from "@mui/x-data-grid";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { stockApi, type StockAvailabilityDto } from "@/api/endpoints";
import { errorMessage } from "@/api/client";
import { useAuth } from "@/store/auth";
import { EditBatchDialog } from "@/features/inventory/EditBatchDialog";

function buildColumns(
  canEdit: boolean,
  onEdit: (row: StockAvailabilityDto) => void,
): GridColDef<StockAvailabilityDto>[] {
  const cols: GridColDef<StockAvailabilityDto>[] = [
    { field: "sku", headerName: "SKU", width: 120 },
    { field: "itemName", headerName: "Item", flex: 1, minWidth: 200 },
    { field: "batchNo", headerName: "Batch", width: 130 },
    {
      field: "expiryDate",
      headerName: "Expiry",
      width: 140,
      renderCell: (p) => {
        if (!p.value) return "—";
        const d = dayjs(p.value);
        const daysLeft = d.diff(dayjs(), "day");
        const color: "default" | "warning" | "error" =
          daysLeft < 0 ? "error" : daysLeft < 90 ? "warning" : "default";
        return <Chip size="small" label={d.format("YYYY-MM-DD")} color={color} variant="outlined" />;
      },
    },
    { field: "warehouseName", headerName: "Warehouse", width: 150 },
    { field: "onHandQty", headerName: "On hand", type: "number", width: 100 },
    { field: "reservedQty", headerName: "Reserved", type: "number", width: 100 },
    {
      field: "availableQty",
      headerName: "Available",
      type: "number",
      width: 110,
      // Out-of-stock is a state, so it reads from the semantic palette rather than a fixed red:
       // the old literals were tuned for light mode and stayed dark on the dark canvas.
      renderCell: (p) => (
        <Box
          component="strong"
          sx={{ color: p.value <= 0 ? "error.main" : "success.main" }}
        >
          {p.value}
        </Box>
      ),
    },
    { field: "costPrice", headerName: "Cost", type: "number", width: 100 },
    {
      field: "mrp",
      headerName: "MRP",
      type: "number",
      width: 110,
      // An unpriced batch is not a zero-rupee pack — it is a batch that bills at the item default
      // and whose sale ceiling is never checked. Worth saying so where somebody can act on it.
      renderCell: (p) => p.value > 0
        ? p.value
        : (
          <Tooltip title="No MRP on this batch — it bills at the item's default and the sale ceiling is not enforced">
            <Chip size="small" label="not set" color="warning" variant="outlined" />
          </Tooltip>
        ),
    },
  ];

  if (canEdit) {
    cols.push({
      field: "actions",
      headerName: "",
      width: 60,
      sortable: false,
      filterable: false,
      renderCell: (p) => (
        <Tooltip title="Correct MRP or expiry">
          <IconButton size="small" onClick={() => onEdit(p.row)}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    });
  }

  return cols;
}

export function StockPage() {
  const canEdit = useAuth(s => s.hasPermission("inventory.batch.edit"));
  const [editing, setEditing] = useState<StockAvailabilityDto | null>(null);

  const query = useQuery({
    queryKey: ["stock-availability"],
    queryFn: () => stockApi.availability(),
  });

  const unpriced = (query.data ?? []).filter(r => r.mrp <= 0).length;

  return (
    <Box>
      <PageHeader breadcrumbs={["Inventory"]} title="Stock availability" />

      <Paper sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Available = Σ(ledger entries) − Σ(active reservations). All quantities are derived from the
          immutable inventory ledger, never stored on the batch row.
        </Typography>

        {unpriced > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }} data-testid="stock-unpriced-batches">
            <b>{unpriced} batch{unpriced === 1 ? "" : "es"} carry no MRP.</b> Each bills at its
            item&apos;s default price, and the ceiling that stops a sale above the printed MRP is not
            enforced on them. {canEdit
              ? "Set the price from the pack using the edit action on the row."
              : "Ask someone with batch-edit rights to set the price from the pack."}
          </Alert>
        )}

        {query.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(query.error)}</Alert>}

        <Box sx={{ height: 600 }}>
          <DataGrid
            rows={query.data ?? []}
            columns={buildColumns(canEdit, setEditing)}
            loading={query.isLoading}
            disableRowSelectionOnClick
            getRowId={(row) => `${row.batchId}-${row.warehouseId}`}
            initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
            pageSizeOptions={[10, 25, 50, 100]}
          />
        </Box>
      </Paper>

      <EditBatchDialog batch={editing} onClose={() => setEditing(null)} />
    </Box>
  );
}
